import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Search, ShieldCheck, Crown, Users, Trash2, Ban, Star, Coins } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { SortableHead, toggleSort, sortRows } from "../../components/SortableHead";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const roleLevel = (u) => (u?.is_general_admin ? 3 : u?.is_super_admin ? 2 : u?.is_admin ? 1 : 0);

const RoleBadge = ({ u }) => (
  <span className="inline-flex flex-wrap items-center gap-1.5">
    {u.is_general_admin ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white bg-[#1A1A1A] border border-[#1A1A1A] rounded-none px-2.5 py-1">
        <Star className="w-3 h-3" /> General Admin
      </span>
    ) : u.is_super_admin ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#8A6D2F] bg-[#D4B36A]/15 border border-[#D4B36A]/40 rounded-none px-2.5 py-1">
        <Crown className="w-3 h-3" /> Super Admin
      </span>
    ) : u.is_admin ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#2D6A4F] bg-[#EAF2EC] border border-[#2D6A4F]/20 rounded-none px-2.5 py-1">
        <ShieldCheck className="w-3 h-3" /> Admin
      </span>
    ) : (
      <span className="text-xs text-[#6E675F]">Customer</span>
    )}
    {u.is_blocked && (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#B42318] bg-[#FBEAEA] border border-[#B42318]/30 rounded-none px-2.5 py-1" data-testid={`blocked-badge-${u.email}`}>
        <Ban className="w-3 h-3" /> Blocked
      </span>
    )}
  </span>
);

export const AdminUsers = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [sort, setSort] = useState({ field: null, dir: "asc" });

  const myLevel = roleLevel(me);
  const isSuperPlus = myLevel >= 2;
  const isGeneral = myLevel >= 3;

  const load = () => {
    api.get("/admin/users")
      .then(({ data }) => setUsers(data))
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const patchRow = (userId, patch) =>
    setUsers((prev) => prev.map((x) => (x.user_id === userId ? { ...x, ...patch } : x)));

  const act = async (u, fn) => {
    setSavingId(u.user_id);
    try {
      await fn();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setSavingId(null);
    }
  };

  const toggleAdmin = (u, checked) => act(u, async () => {
    await api.put(`/admin/users/${u.user_id}/admin`, { is_admin: checked });
    patchRow(u.user_id, { is_admin: checked, ...(checked ? {} : { is_super_admin: false }) });
    toast.success(`${u.email} is ${checked ? "now an admin" : "no longer an admin"}`);
  });

  const toggleSuper = (u, checked) => act(u, async () => {
    await api.put(`/admin/users/${u.user_id}/super`, { is_super_admin: checked });
    patchRow(u.user_id, { is_super_admin: checked, ...(checked ? { is_admin: true } : {}) });
    toast.success(`${u.email} is ${checked ? "now a super admin" : "no longer a super admin"}`);
  });

  const toggleBlock = (u) => {
    const blocking = !u.is_blocked;
    const msg = blocking
      ? `Block ${u.email}? They will be signed out immediately and unable to sign in or order until unblocked.`
      : `Unblock ${u.email}? They will be able to sign in again.`;
    if (!window.confirm(msg)) return;
    act(u, async () => {
      await api.put(`/admin/users/${u.user_id}/block`, { is_blocked: blocking });
      patchRow(u.user_id, { is_blocked: blocking });
      toast.success(`${u.email} ${blocking ? "blocked" : "unblocked"}`);
    });
  };

  const deleteUser = (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? Their account, sessions and stock alerts are removed. Order history is kept for records.`)) return;
    act(u, async () => {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast.success(`${u.email} deleted`);
    });
  };

  const adjustPoints = (u) => {
    const raw = window.prompt(
      `Adjust reward points for ${u.email}\nCurrent balance: ${u.points} pts\n\nEnter amount — positive to grant, negative to deduct (e.g. 500 or -200):`
    );
    if (raw === null) return;
    const amount = parseInt(raw, 10);
    if (!amount) {
      toast.error("Enter a non-zero whole number");
      return;
    }
    const reason = window.prompt("Reason (optional — recorded in the activity log):") || "";
    act(u, async () => {
      const { data } = await api.put(`/admin/users/${u.user_id}/points`, { amount, reason });
      patchRow(u.user_id, { points: data.points });
      toast.success(`${u.email} now has ${data.points} pts (${data.adjusted_by >= 0 ? "+" : ""}${data.adjusted_by})`);
    });
  };

  const canManage = (u) => u.user_id !== me?.user_id && roleLevel(u) < myLevel;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) => u.email.toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q))
    : users;
  const visible = sortRows(filtered, sort, {
    user: (u) => u.name || u.email,
    joined: (u) => u.created_at || "",
    orders: (u) => u.orders_count,
    spent: (u) => u.total_spent,
    points: (u) => u.points,
    role: (u) => roleLevel(u),
  });

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-users-section">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-[#8C7B6B] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="admin-user-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9"
          />
        </div>
        <p className="text-sm text-[#6E675F] flex items-center gap-2" data-testid="admin-users-count">
          <Users className="w-4 h-4" /> {filtered.length} user{filtered.length === 1 ? "" : "s"}
        </p>
      </div>

      {!isSuperPlus && (
        <p className="text-xs text-[#6E675F] mb-4" data-testid="admin-users-readonly-note">
          Admins can block customers. Changing permissions or deleting users requires super admin or above.
        </p>
      )}

      <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-x-auto">
        <Table data-testid="admin-users-table">
          <TableHeader>
            <TableRow>
              <SortableHead label="User" active={sort.field === "user"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "user"))} testid="users-sort-user" />
              <SortableHead label="Joined" active={sort.field === "joined"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "joined"))} testid="users-sort-joined" />
              <SortableHead label="Orders" className="text-right" active={sort.field === "orders"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "orders"))} testid="users-sort-orders" />
              <SortableHead label="Spent" className="text-right" active={sort.field === "spent"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "spent"))} testid="users-sort-spent" />
              <SortableHead label="Points" className="text-right" active={sort.field === "points"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "points"))} testid="users-sort-points" />
              <SortableHead label="Role" active={sort.field === "role"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "role"))} testid="users-sort-role" />
              {isSuperPlus && <TableHead className="text-right">Admin</TableHead>}
              {isGeneral && <TableHead className="text-right">Super Admin</TableHead>}
              <TableHead className="text-right">Block</TableHead>
              {isSuperPlus && <TableHead className="text-right">Delete</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((u) => (
              <TableRow key={u.user_id} data-testid={`admin-user-row-${u.email}`} className={u.is_blocked ? "bg-[#FBEAEA]/40" : ""}>
                <TableCell>
                  <p className="font-medium text-[#1A1A1A]">{u.name || "—"}</p>
                  <p className="text-xs text-[#6E675F]">{u.email}</p>
                </TableCell>
                <TableCell className="text-[#6E675F] text-sm">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell className="text-right">{u.orders_count}</TableCell>
                <TableCell className="text-right">${u.total_spent.toFixed(2)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <span data-testid={`admin-user-points-${u.email}`}>{u.points}</span>
                  {isSuperPlus && canManage(u) && (
                    <button
                      data-testid={`adjust-points-${u.email}`}
                      onClick={() => adjustPoints(u)}
                      disabled={savingId === u.user_id}
                      className="ml-1.5 text-[#8A6D2F] hover:bg-[#F6F1E7] rounded-md p-1.5 transition-colors disabled:opacity-40 align-middle"
                      title={`Grant or deduct points for ${u.email}`}
                      aria-label={`Adjust points for ${u.email}`}
                    >
                      <Coins className="w-3.5 h-3.5" />
                    </button>
                  )}
                </TableCell>
                <TableCell><RoleBadge u={u} /></TableCell>
                {isSuperPlus && (
                  <TableCell className="text-right">
                    <Switch
                      checked={u.is_admin}
                      disabled={!canManage(u) || savingId === u.user_id}
                      onCheckedChange={(checked) => toggleAdmin(u, checked)}
                      data-testid={`admin-toggle-${u.email}`}
                      aria-label={`Toggle admin access for ${u.email}`}
                    />
                  </TableCell>
                )}
                {isGeneral && (
                  <TableCell className="text-right">
                    <Switch
                      checked={u.is_super_admin}
                      disabled={!canManage(u) || savingId === u.user_id}
                      onCheckedChange={(checked) => toggleSuper(u, checked)}
                      data-testid={`super-toggle-${u.email}`}
                      aria-label={`Toggle super admin access for ${u.email}`}
                    />
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <button
                    data-testid={`block-toggle-${u.email}`}
                    onClick={() => toggleBlock(u)}
                    disabled={!canManage(u) || savingId === u.user_id}
                    className={`rounded-md p-2 transition-colors disabled:opacity-30 disabled:hover:bg-transparent ${
                      u.is_blocked ? "text-[#2D6A4F] hover:bg-[#EAF2EC]" : "text-[#B45309] hover:bg-[#FEF3E2]"
                    }`}
                    title={!canManage(u) ? "You can only block users below your admin level" : u.is_blocked ? `Unblock ${u.email}` : `Block ${u.email}`}
                    aria-label={`${u.is_blocked ? "Unblock" : "Block"} ${u.email}`}
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                </TableCell>
                {isSuperPlus && (
                  <TableCell className="text-right">
                    <button
                      data-testid={`admin-delete-user-${u.email}`}
                      onClick={() => deleteUser(u)}
                      disabled={!canManage(u) || savingId === u.user_id}
                      className="text-[#B42318] hover:bg-[#FBEAEA] rounded-md p-2 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                      title={!canManage(u) ? "You can only delete users below your admin level" : `Permanently delete ${u.email}`}
                      aria-label={`Delete user ${u.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
